import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import heroBg from "@/assets/hero-bg.jpg";
import heroVideo from "@/assets/hero-video.mp4.asset.json";

const Hero = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [enableParallax, setEnableParallax] = useState(false);
  // Normalized pointer offset from section center, range -1..1
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    setReducedMotion(prefersReducedMotion);

    const finePointer = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    ).matches;
    const shouldParallax = !prefersReducedMotion && finePointer;
    setEnableParallax(shouldParallax);
    if (!shouldParallax) return;

    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      setPointer({ x: nextX, y: nextY });
      ticking = false;
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const nx = (e.clientX - rect.left - halfW) / halfW;
      const ny = (e.clientY - rect.top - halfH) / halfH;
      nextX = Math.min(1, Math.max(-1, nx));
      nextY = Math.min(1, Math.max(-1, ny));
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      nextX = 0;
      nextY = 0;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const { x: nx, y: ny } = pointer;
  const blobTransition =
    "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out";

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100svh] md:min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background video (image fallback when reduced motion) */}
      {reducedMotion ? (
        <img
          src={heroBg}
          alt={t("hero.bgAlt")}
          className="absolute inset-0 w-full h-full object-cover object-[50%_40%] md:object-center opacity-40"
          loading="eager"
          width="1920"
          height="1080"
        />
      ) : (
        <video
          src={heroVideo.url}
          poster={heroBg}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[50%_40%] md:object-center opacity-40"
        />
      )}

      {/* Mesh gradient layer */}
      <div className="absolute inset-0 bg-gradient-mesh" />

      {/* Floating orbs — driven by mouse movement on desktop */}
      <div
        aria-hidden
        className="absolute -top-24 -left-24 md:-top-32 md:-left-32 w-[18rem] h-[18rem] md:w-[24rem] md:h-[24rem] lg:w-[28rem] lg:h-[28rem] rounded-full bg-primary/15 md:bg-primary/25 blur-3xl will-change-transform"
        style={{
          transform: enableParallax
            ? `translate3d(${nx * 70}px, ${ny * 60}px, 0) scale(${1 + Math.abs(nx) * 0.08})`
            : undefined,
          filter: enableParallax ? `blur(48px) hue-rotate(${nx * 12}deg)` : undefined,
          transition: enableParallax ? blobTransition : undefined,
        }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 -right-24 md:-right-32 w-[20rem] h-[20rem] md:w-[26rem] md:h-[26rem] lg:w-[32rem] lg:h-[32rem] rounded-full blur-3xl will-change-transform"
        style={{
          backgroundColor: `hsl(var(--accent-violet) / ${enableParallax ? 0.32 : 0.22})`,
          transform: enableParallax
            ? `translate3d(${nx * -90}px, ${ny * -75}px, 0) scale(${1 + Math.abs(ny) * 0.1})`
            : undefined,
          filter: enableParallax ? `blur(48px) hue-rotate(${nx * -16}deg)` : undefined,
          transition: enableParallax ? blobTransition : undefined,
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-1/3 w-[16rem] h-[16rem] md:w-[20rem] md:h-[20rem] lg:w-[24rem] lg:h-[24rem] rounded-full bg-primary/10 md:bg-primary/15 blur-3xl will-change-transform"
        style={{
          transform: enableParallax
            ? `translate3d(${nx * 50}px, ${ny * -55}px, 0) scale(${1 + Math.abs(nx) * 0.07})`
            : undefined,
          filter: enableParallax ? `blur(48px) hue-rotate(${ny * 10}deg)` : undefined,
          transition: enableParallax ? blobTransition : undefined,
        }}
      />

      {/* Subtle dot grid for texture */}
      <div className="absolute inset-0 bg-grid-faint opacity-30 md:opacity-50" />

      {/* Soft radial vignette behind headline for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-radial-glow opacity-60 pointer-events-none"
      />

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 lg:h-40 bg-gradient-to-t from-background to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center pb-[env(safe-area-inset-bottom)]">
        <h1
          className="max-w-4xl mx-auto text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight animate-fade-in-up"
          style={{ animationDelay: "120ms" }}
        >
          {t("hero.titlePre")}{" "}
          <span className="text-gradient-brand">{t("hero.titleAccent")}</span>
        </h1>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-6 md:bottom-8 lg:bottom-10 left-1/2 -translate-x-1/2 z-10"
        aria-hidden="true"
      >
        <div className="w-0 h-0 animate-scroll-nudge"
          style={{
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '12px solid hsl(var(--muted-foreground) / 0.6)',
          }}
        />
      </div>

    </section>
  );
};

export default Hero;
